import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ReactComponent as Plus } from '../../../assets/icons/plus.svg';
import { ReactComponent as Facility } from '../../../assets/icons/sidebar/facility.svg';
import { ReactComponent as DotCircle } from '../../../assets/icons/tables/dot-circle.svg';
import { ReactComponent as ExpandIcon } from '../../../assets/icons/tables/expand-icon.svg';
// import { ReactComponent as NewTabIcon } from '../../../assets/icons/tables/new-tab.svg';
import { appCurrentBranding } from '../../../config/branding';
import DefaultApiLimit from '../../../config/defaultLimit';
import { branding } from '../../../config/enum';
import {
  AccountsInterFace,
  RequestAccountInterface,
} from '../../../interface/account-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableAccountManagementFilterApplied,
  fetchAccountManagementFilters,
  fetchAllAccounts,
  setSortData,
  updateAccountManagementFilters,
} from '../../../redux/slices/AccountManagementSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ConfirmBox from '../../../shared-components/modal-box/ConfirmBox';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import FacilityDetailsSidePanel from '../../../shared-components/table-side-panel/FacilityDetailsSidePanel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import accountApi from '../../../utils/api/account';

import AddFacilityAccount from './add-facility-account';

interface ModalInterface {
  title: string;
  header: boolean;
  content: JSX.Element;
  rightCorner: string;
  notification: boolean;
}

/**
 * Facility account list
 * @param param0
 * @returns
 */
function FacilityAccountList() {
  const { t } = useTranslation('account');
  const dispatch = useAppDispatch();
  const {
    accounts,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.accounts);
  const navigate = useNavigate();

  const [openStatus, setOpenStatus] = useState<boolean>(false);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [sidePanelOpen, setSidePanelOpen] = useState<boolean>(false);
  const [account, setAccount] = useState<AccountsInterFace>({
    _id: '',
    id: '',
    name: '',
    email: '',
    date: '',
    phone: '',
    tenantId: '',
    isAccountVerified: false,
    documents: [],
    currency: '',
  });
  const [searchParameters, setSearchParameters] = useSearchParams();

  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [modalView, setModalView] = useState<ModalInterface>({
    title: '',
    header: false,
    content: <div />,
    rightCorner: '',
    notification: false,
  });

  /**
   * Fetch the account details in redus
   */
  const fetchData = (data: RequestAccountInterface) => {
    if (!loading) {
      dispatch(fetchAllAccounts(data));
    }
  };
  /**
   * Modal box close function
   */
  const closeModal = (message: string) => {
    if (message) {
      setOpenStatus(true);
      setModalView({
        title: '',
        header: false,
        content: (
          <SuccessNotification
            title={message}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setOpenStatus(false);
            }}
          />
        ),
        rightCorner: '',
        notification: true,
      });
    } else {
      setOpenConfirm(false);
      setOpenStatus(false);
    }
  };

  /**
   * Add account function
   */
  const addAccount = () => {
    // searchParameters.delete('facilityId');
    // setSearchParameters(searchParameters);
    setSidePanelOpen(true);
  };

  /**
   * Table pagination function
   * @param value
   */
  const pageAndLimitChange = (value: RequestAccountInterface) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, ...value, sortField, sortBy });
  };

  /**
   * View Facility function
   */
  // const viewFacilityFunction = (currentData: any) => {
  //   const accountDetails = accounts.find((value: AccountsInterFace) => value.id === currentData);
  //   if (accountDetails && Object.keys(accountDetails).length > 0) {
  //     const request: AccessFacilityPortal = {
  //       tenantId: accountDetails.tenantId,
  //     };

  //     authApi
  //       .accessFacilityPortal(request)
  //       .then((response) => {
  //         const responseData = response?.data;

  //         window.open(
  //           `${window.origin.replace('admin', 'facility')}/login/credentials?accessToken=${responseData.access.token}&accessTokenExpires=${responseData.access.expires}&refreshToken=${responseData.refresh.token}&refreshTokenExpires=${responseData.refresh.expires}&isEmailVerified=${responseData.isEmailVerified}&isPhoneNumberVerified=${responseData.isPhoneNumberVerified}&isInitialPasswordReset=${responseData.isInitialPasswordReset}&accountVerificationStatus=${responseData.accountVerificationStatus}`,
  //           '_blank',
  //         );
  //       })
  //       .catch((error) => {
  //         dispatch(
  //           setNotificationMessage({
  //             message: error?.response?.data?.message,
  //             status: true,
  //             type: 'error',
  //             code: error?.response?.data?.code,
  //           }),
  //         );
  //       });
  //   }
  // };

  /**
   * Delete confirm function
   */
  const deleteConfirmFunction = () => {
    accountApi.deleteAccount(account.tenantId).then(() => {
      closeModal(`${t('accountDeletedSuccessfully')}...`);
    });
  };

  const column = [
    {
      label: `${t('facilityId')}`,
      accessorKey: 'accountCustomId' as keyof AccountsInterFace,
      columnWidth: 170,
      /**
       * No of stations
       */
      customData: (currentRow: AccountsInterFace) => (
        <p className="hyper-link-coloum">{currentRow.accountCustomId || '-'}</p>
      ),
    },

    {
      label: `${t('facilityOwnerName')}`,
      accessorKey: 'accountName' as keyof AccountsInterFace,
      sortAllow: true,
    },
    {
      label: `${t('email')}`,
      accessorKey: 'email' as keyof AccountsInterFace,
      sortAllow: false,
    },
    {
      label: `${t('phoneNumber')}`,
      accessorKey: 'phoneNumberCountryCode' as keyof AccountsInterFace,
      sortAllow: false,
    },
    ...(appCurrentBranding === branding.VOLTIE
      ? [
          {
            label: t('stripeId'),
            accessorKey: 'stripePaymentId' as keyof AccountsInterFace,
            sortAllow: false,
          },
        ]
      : []),
    {
      label: `${t('noOfStations')}`,
      accessorKey: 'stationCount' as keyof AccountsInterFace,
      sortAllow: true,
      /**
       * No of stations
       */
      customData: (currentRow: AccountsInterFace) => <p>{currentRow.stationCount || 0}</p>,
    },
    {
      label: `${t('noOfChargers')}`,
      accessorKey: 'totalDeviceCount' as keyof AccountsInterFace,
      sortAllow: true,
      /**
       * No of Chargers
       */
      customData: (currentRow: AccountsInterFace) => <p>{currentRow.totalDeviceCount || 0}</p>,
    },
    {
      label: `${t('onlineChargers')}`,
      accessorKey: 'activeDeviceCount' as keyof AccountsInterFace,
      sortAllow: true,
      /**
       *Online Chargers
       */
      customData: (currentRow: AccountsInterFace) => <p>{currentRow?.activeDeviceCount || 0}</p>,
    },
    {
      label: `${t('kwhConsumedPaid')}`,
      accessorKey: 'powerConsumed' as keyof AccountsInterFace,
      sortAllow: true,
      /**
       * KW Consumed
       */
      customData: (currentRow: AccountsInterFace) => {
        const value = Number(currentRow?.powerConsumed) / 1000;
        return (
          <div className="value-container">
            <p className="value">{ToFixedValue(value)}</p>
            <span>kWh</span>
          </div>
        );
      },
    },
    {
      label: `${t('verificationStatus')}`,
      accessorKey: 'status' as keyof AccountsInterFace,
      sortAllow: false,
      /**
       * Data custom function
       * @param currentRow
       */
      customData: (currentRow: AccountsInterFace) => {
        let output = <div />;

        switch (currentRow.status) {
          case 'pending':
            output = (
              <div className="tag-warning-label">
                <DotCircle /> <span>{t('unverified')}</span>
              </div>
            );
            break;
          case 'approved':
            output = (
              <div className="tag-success-label">
                <DotCircle /> <span>{t('verified')}</span>
              </div>
            );
            break;
          case 'rejected':
            output = (
              <div className="tag-danger-label">
                <DotCircle /> <span>{t('rejected')}</span>
              </div>
            );
            break;

          default:
            break;
        }

        return output;
      },
    },
  ];

  /**
   * Function to expand the facility
   * @param facilityId
   */
  const expandFunction = (facilityId: any) => {
    const accountDetails = accounts.find(
      (value: AccountsInterFace) => value.tenantId === facilityId,
    );

    navigate(
      `./payout-management?accountCustomId=${
        accountDetails?.accountCustomId
      }&tenantId=${accountDetails?.tenantId}`,
    );
  };
  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Column definitions for the table
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('facilityId'), accessorKey: 'accountCustomId' },
    { label: t('facilityOwnerName'), accessorKey: 'accountName' },
    { label: t('email'), accessorKey: 'email' },
    { label: t('phoneNumber'), accessorKey: 'phoneNumberCountryCode' },
    { label: t('noOfStations'), accessorKey: 'stationCount' },
    { label: t('noOfChargers'), accessorKey: 'totalDeviceCount' },
    { label: t('onlineChargers'), accessorKey: 'activeDeviceCount' },
    { label: t('kwhConsumedPaid'), accessorKey: 'powerConsumed' },
    { label: t('verificationStatus'), accessorKey: 'status' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function (without field filtering)
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload = {
      ...queryParameters,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;
    setExportLoader(true);
    accountApi
      .exportData(payload)
      .then((response) => {
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: AccountsInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'activeDeviceCount':
                return row.activeDeviceCount || 0;
              case 'powerConsumed':
                return `${ToFixedValue(Number(row?.powerConsumed) / 1000)} kWh`;
              case 'status':
                switch (row.submission?.status) {
                  case 'pending':
                    return t('unverified');
                  case 'approved':
                    return t('verified');
                  case 'rejected':
                    return t('rejected');
                  default:
                    return t('notAvailable');
                }

              case 'stationCount':
                return row.stationCount || 0;
              case 'totalDeviceCount':
                return row.totalDeviceCount || 0;
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, `${t('accountList')}.csv`);
          } else {
            exportPDF(headers, rows, `${t('accountList')}.pdf`);
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };
  /**
   * Function to open side panel on row click
   */
  const handleOpenSidePanelOnRowClick = (tenantId: string) => {
    // Set or update the logId param
    searchParameters.set('tenantId', tenantId);
    // Apply the updated search params to the URL
    setSearchParameters(searchParameters);

    dispatch(
      setPageTitle({
        title: t('partners'),
        icon: <Facility />,
        count: undefined,
      }),
    );
  };
  useEffect(() => {
    if (isFilterUpdated || accounts.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        start: '',
        ...queryParameters,
      });
      dispatch(disableAccountManagementFilterApplied());
    }
  }, [filters]);

  useEffect(() => {
    if (!sidePanelOpen) {
      dispatch(
        setPageTitle({
          title: t('partners'),
          icon: <Facility />,
          count: totalResults,
        }),
      );
    }
  }, [totalResults, sidePanelOpen, t, dispatch]);

  useEffect(() => {
    if (searchParameters.get('tenantId')) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('tenantId');
      setSearchParameters(parameters, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (!searchParameters.get('tenantId')) {
      setIsEditMode(false);
    }
  }, [searchParameters]);

  return (
    <div>
      <ModalBox
        open={openStatus}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setOpenStatus(false)}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />
      <ConfirmBox
        open={openConfirm}
        title={t('verifyAccountTitle')}
        subTitle={t('verifyAccountSubtitle')}
        conformSubmit={deleteConfirmFunction}
        closeModal={() => {
          setOpenConfirm(false);
          setOpenStatus(false);
          setAccount({
            _id: '',
            id: '',
            name: '',
            email: '',
            date: '',
            phone: '',
            tenantId: '',
            isAccountVerified: false,
            currency: '',
          });
        }}
      />
      <CustomTable
        version="v2"
        header={column}
        data={accounts}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        serialNo
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageAndLimitChange}
        onHiddenColumnsChange={setHiddenColumns}
        actionButton
        height={window.innerHeight >= 700 ? 375 : 352}
        filters={filters}
        filtersToDispatch={updateAccountManagementFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchAccountManagementFilters}
        sizeChangeButtonRequired
        actionDropdown
        exportData={accounts}
        exportLoader={exportLoader}
        exportDataPdfOrCSV={(permissionCheck('export_account') as any) && exportDataPdfOrCSV}
        rowClickEvent={handleOpenSidePanelOnRowClick}
        rowClickParamsKey="tenantId"
        sidePanelEnabled={sidePanelOpen}
        sidePanelManualContent={
          <AddFacilityAccount
            closeModal={closeModal}
            sidebarClosed={() => setSidePanelOpen(false)}
          />
        }
        onSidePanelClose={() => {
          setSidePanelOpen(false);

          dispatch(
            setPageTitle({
              title: t('partners'),
              icon: <Facility />,
              count: totalResults, // ✅ restore count
            }),
          );
        }}
        sidePanelTopLeftContent={
          <div className="side-panel-header-left-icons">
            {!isEditMode && !sidePanelOpen && (
              <>
                <div
                  role="presentation"
                  className="access-facility"
                  onClick={() => expandFunction(searchParameters.get('tenantId'))}>
                  <ExpandIcon />
                </div>
                {/* <div
                  role="presentation"
                  className="access-facility"
                  onClick={() => viewFacilityFunction(searchParameters.get('facilityId'))}>
                  <NewTabIcon />
                </div> */}
              </>
            )}
          </div>
        }
        rowClickSidePanel={
          <FacilityDetailsSidePanel
            actionActive
            onEditModeChange={setIsEditMode}
            fetchIdFrom="queryParams"
          />
        }
        rightTopContent={
          <div className="table-bottom-button-list">
            {(permissionCheck('add_account') as any) && (
              <div className="table-button-item">
                <MyButton
                  label={t('addAccount')}
                  buttonType="submit"
                  onClickFunc={addAccount}
                  svgIcon={<Plus />}
                />
              </div>
            )}
          </div>
        }
        refreshRequired
        handleRefresh={handleRefresh}
        hoverLabel={t('refresh')}
        ReferenceCurrentPages={handleRefresh}
      />
    </div>
  );
}

export default FacilityAccountList;
